/**
 * Shared canvas-ratio / background-color presets and small pure helpers used
 * by both the Background Editor and the Shadow Editor. Kept in one place so
 * the two editors can never drift apart on what "1:1" or "浅灰" actually
 * mean — the Shadow Editor MUST reproduce the exact canvas the Background
 * Editor produced (spec 三).
 */

export interface RatioPreset {
  key: string
  label: string
  w: number
  h: number
}

export const RATIO_PRESETS: RatioPreset[] = [
  { key: '1:1', label: '1:1 正方形', w: 2000, h: 2000 },
  { key: '4:3', label: '4:3', w: 2400, h: 1800 },
  { key: '3:4', label: '3:4', w: 1800, h: 2400 },
  { key: '16:9', label: '16:9', w: 3200, h: 1800 },
  { key: '9:16', label: '9:16', w: 1800, h: 3200 },
  { key: 'a4p', label: 'A4 竖版', w: 2100, h: 2970 },
  { key: 'a4l', label: 'A4 横版', w: 2970, h: 2100 },
]

export type BgPreset = 'white' | 'light-gray' | 'mid-gray' | 'dark-gray' | 'black' | 'custom'

export const BG_PRESET_COLOR: Record<Exclude<BgPreset, 'custom'>, string> = {
  white: '#ffffff',
  'light-gray': '#f0f0f0',
  'mid-gray': '#bfbfbf',
  'dark-gray': '#595959',
  black: '#000000',
}

export const BG_PRESET_LABEL: Record<BgPreset, string> = {
  white: '白色',
  'light-gray': '浅灰',
  'mid-gray': '中灰',
  'dark-gray': '深灰',
  black: '黑色',
  custom: '自定义颜色',
}

export type BgMode = 'solid' | 'radial' | 'vertical' | 'horizontal'

export const BG_MODE_OPTIONS: Array<{ value: BgMode; label: string }> = [
  { value: 'solid', label: '纯色' },
  { value: 'radial', label: '径向渐变' },
  { value: 'vertical', label: '上下渐变' },
  { value: 'horizontal', label: '左右渐变' },
]

/**
 * Gradient direction/strength schema — bumped when `gradientIntensity`'s
 * *meaning* changes, not just its rendering. v2 (current): a single signed
 * value in [-100, 100] centered on 0 = solid/no-gradient, replacing the old
 * v1 one-directional 0-100 slider that could never express "which way" the
 * gradient ran (see `normalizeGradientValue` for the v1 -> v2 migration).
 */
export const GRADIENT_SCHEMA_VERSION = 2
export const GRADIENT_MIN = -100
export const GRADIENT_MAX = 100
export const GRADIENT_DEFAULT = 0

/** Max lighten/darken amount (0-1, fraction toward white/black) reached at
 * |value| = 100. Deliberately asymmetric — darkening a color perceptually
 * "reads" faster than lightening it, so a slightly smaller max keeps both
 * ends feeling comparably strong instead of the dark end looking harsher. */
const MAX_LIGHT_AMOUNT = 0.5
const MAX_DARK_AMOUNT = 0.42

/**
 * v1 -> v2 migration: v1 only ever had ONE rendered direction per mode
 * (baked into the old gradient code, not user-chosen), so migrating must
 * reproduce that same *visual* direction under the new signed scheme, not
 * just reinterpret the raw number:
 *   - v1 vertical/horizontal always rendered "start edge lighter, end edge
 *     darker" (top-light/bottom-dark, left-light/right-dark) — that is the
 *     v2 *negative* direction, so the sign flips.
 *   - v1 radial always rendered "center lighter, edge darker" — that IS the
 *     v2 *positive* direction, so the sign is kept as-is.
 * A template/chain that already carries `gradientVersion === 2` is trusted
 * as-is (a user may have deliberately dialed in a positive vertical value,
 * which must never be silently re-flipped on every subsequent load).
 */
export function normalizeGradientValue(mode: BgMode, rawValue: number | undefined, version: number | undefined): number {
  const v = typeof rawValue === 'number' && Number.isFinite(rawValue) ? rawValue : GRADIENT_DEFAULT
  if (version === GRADIENT_SCHEMA_VERSION) {
    return clamp(v, GRADIENT_MIN, GRADIENT_MAX)
  }
  const legacyMagnitude = clamp(v, 0, 100)
  if (legacyMagnitude === 0) return 0
  return mode === 'radial' ? legacyMagnitude : -legacyMagnitude
}

export const DEFAULT_SCALE_PCT = 66
export const MIN_SCALE_PCT = 20
export const MAX_SCALE_PCT = 150

export function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v))
}

/**
 * Normalize any CSS color string the editor may have stored into `#rrggbb`.
 * Ant Design's ColorPicker `onChange` second argument is documented as a hex
 * string but can actually be `color.toCssString()` (`rgb(...)`/`rgba(...)`)
 * depending on the popover's format toggle — see antd's
 * AggregationColor.toCssString. Solid canvas fills accept that form, but the
 * gradient pixel path and the backend's `_hex_to_rgb` both expect `#rrggbb`.
 * Without this, picking a custom color then enabling any gradient silently
 * re-renders as black (`parseInt('rgb(...)', 16)` is `NaN`, `NaN >> 16`
 * coerces to `0`) — the "custom color -> gradient -> black" bug (Bug B,
 * custom-color path), distinct from (and in addition to) the signed-offset
 * root cause fixed in `combinedGradientOffset`.
 */
export function normalizeCssColorToHex(input: string | null | undefined): string {
  const raw = (input || '').trim()
  if (!raw) return '#ffffff'
  const rgbMatch = raw.match(/^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*[0-9.]+\s*)?\)$/i)
  if (rgbMatch) {
    return rgbToHex(Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3]))
  }
  const hexBody = raw.startsWith('#') ? raw.slice(1) : raw
  if (/^[0-9a-f]{3}$/i.test(hexBody)) {
    return `#${hexBody.split('').map((c) => c + c).join('').toLowerCase()}`
  }
  if (/^[0-9a-f]{6}$/i.test(hexBody)) {
    return `#${hexBody.toLowerCase()}`
  }
  return '#ffffff'
}

/** Parses a CSS color string into [r, g, b], 0-255 each — accepts BOTH
 * `#rgb`/`#rrggbb` hex AND `rgb(r, g, b)`/`rgba(r, g, b, a)` via
 * `normalizeCssColorToHex` (MUST mirror the backend's `_hex_to_rgb` exactly —
 * same accepted formats — spec 三.4: "预览和最终生成必须一致"). */
export function hexToRgb(hex: string): [number, number, number] {
  const normalized = normalizeCssColorToHex(hex)
  const m = normalized.replace('#', '')
  const n = parseInt(m || 'ffffff', 16)
  if (!Number.isFinite(n)) return [255, 255, 255]
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

export function rgbToHex(r: number, g: number, b: number) {
  const c = (v: number) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')
  return `#${c(r)}${c(g)}${c(b)}`
}

/** Lightens (positive amt) or darkens (negative amt) a hex color by shifting
 * each channel toward white/black. */
export function shadeHex(hex: string, amt: number): string {
  const [r, g, b] = hexToRgb(hex)
  const target = amt >= 0 ? 255 : 0
  const t = Math.abs(amt)
  return rgbToHex(r + (target - r) * t, g + (target - g) * t, b + (target - b) * t)
}

/**
 * Paints the 色底图 background: solid fill, or a signed (-100..100) gradient
 * around `baseColor`. This is the single canonical implementation shared by
 * the Background Editor's own canvas AND (via `shadowRender.ts`) the Shadow
 * Editor's inherited-background preview, so the two can never visually
 * diverge (spec 九). `value` must already be in the current v2 signed scheme
 * — callers reading persisted params are responsible for running them
 * through `normalizeGradientValue` first; this function never re-migrates.
 *
 * Direction contract (spec 三/四/六):
 *   vertical:   value < 0 -> 上浅下深 (top lighter)   | value > 0 -> 上深下浅 (top darker)
 *   horizontal: value < 0 -> 左浅右深 (left lighter)  | value > 0 -> 左深右浅 (left darker)
 *   radial:     value < 0 -> 中心深周边浅 (center darker) | value > 0 -> 中心浅周边深 (center lighter)
 *   value === 0 (any mode): flat solid fill, no gradient object created at all.
 */
export function paintBackground(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  baseColor: string,
  mode: BgMode,
  value: number,
) {
  const v = Math.round(clamp(value, GRADIENT_MIN, GRADIENT_MAX))
  if (mode === 'solid' || v === 0) {
    ctx.fillStyle = baseColor
    ctx.fillRect(0, 0, w, h)
    return
  }
  const strength = Math.abs(v) / 100
  const light = shadeHex(baseColor, strength * MAX_LIGHT_AMOUNT)
  const dark = shadeHex(baseColor, -strength * MAX_DARK_AMOUNT)

  if (mode === 'radial') {
    // Center/edge convention is the OPPOSITE of the linear modes' start/end
    // convention below: positive radial = 中心浅周边深 (center is the LIGHT
    // end), matching the old v1 radial's only rendered direction, whereas
    // positive vertical/horizontal deliberately flips v1's direction (see
    // `normalizeGradientValue`).
    const [centerColor, edgeColor] = v > 0 ? [light, dark] : [dark, light]
    // Aspect-correct ellipse: scale the coordinate system so a circle drawn
    // in local space maps back to an ellipse matching the canvas's own w:h
    // ratio, instead of a fixed circle that either barely reaches the short
    // edges (wide canvases) or overshoots badly (tall canvases).
    ctx.save()
    const maxDim = Math.max(w, h)
    ctx.translate(w / 2, h / 2)
    ctx.scale(w / maxDim, h / maxDim)
    const r = maxDim * 0.75
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r)
    grad.addColorStop(0, centerColor)
    grad.addColorStop(0.5, baseColor)
    grad.addColorStop(1, edgeColor)
    ctx.fillStyle = grad
    ctx.fillRect(-maxDim / 2, -maxDim / 2, maxDim, maxDim)
    ctx.restore()
    return
  }
  // Linear modes: positive => start edge (top/left) is the DARK end;
  // negative => start edge is the LIGHT end.
  const [startColor, endColor] = v > 0 ? [dark, light] : [light, dark]
  const grad =
    mode === 'vertical' ? ctx.createLinearGradient(0, 0, 0, h) : ctx.createLinearGradient(0, 0, w, 0)
  grad.addColorStop(0, startColor)
  grad.addColorStop(0.5, baseColor)
  grad.addColorStop(1, endColor)
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, w, h)
}

/**
 * Composable gradient model (v3) — replaces the old mutually-exclusive
 * `bgMode: 'solid'|'radial'|'vertical'|'horizontal'` with three independent
 * axes that can be toggled and combined freely (spec 三.1). "纯色" is no
 * longer a stored mode; it is simply the state where all three axes are
 * disabled (see `isSolidGradients`).
 */
export interface GradientAxisConfig {
  enabled: boolean
  /** Signed -100..100, same per-axis direction convention as the old v2
   * scheme (see the direction contract on `combinedGradientOffset` below). */
  strength: number
}

export interface BackgroundGradientConfig {
  radial: GradientAxisConfig
  vertical: GradientAxisConfig
  horizontal: GradientAxisConfig
}

function defaultAxis(): GradientAxisConfig {
  return { enabled: false, strength: 0 }
}

export function defaultGradients(): BackgroundGradientConfig {
  return { radial: defaultAxis(), vertical: defaultAxis(), horizontal: defaultAxis() }
}

/** "纯色" (spec 三.3) is a derived state, never a stored flag: true whenever
 * every axis is off, regardless of what strength values are still parked in
 * them (strengths are deliberately preserved across a "纯色" click so
 * re-enabling an axis restores the last value the user had dialed in). */
export function isSolidGradients(g: BackgroundGradientConfig): boolean {
  return !g.radial.enabled && !g.vertical.enabled && !g.horizontal.enabled
}

/** v1/v2 (single exclusive `bgMode` + `gradientIntensity`) -> v3 (composable
 * `gradients`) migration (spec 三.6/十.色底图旧数据). Trusts an existing
 * `gradients` object as-is (already v3) rather than re-deriving it, so a
 * template/chain saved by the current editor is never silently altered on
 * every subsequent load. */
export function migrateBackgroundGradients(params: {
  bgMode?: string
  gradientIntensity?: number
  gradientVersion?: number
  gradients?: Partial<Record<keyof BackgroundGradientConfig, Partial<GradientAxisConfig>>> | null
} | null | undefined): BackgroundGradientConfig {
  if (params?.gradients) {
    const g = params.gradients
    return {
      radial: { ...defaultAxis(), ...g.radial },
      vertical: { ...defaultAxis(), ...g.vertical },
      horizontal: { ...defaultAxis(), ...g.horizontal },
    }
  }
  const out = defaultGradients()
  const mode = (params?.bgMode as BgMode) || 'solid'
  if (mode === 'solid') return out
  const value = normalizeGradientValue(mode, params?.gradientIntensity, params?.gradientVersion)
  if (value === 0) return out
  out[mode] = { enabled: true, strength: value }
  return out
}

/** Max lightness fraction (toward white) a single fully-cranked
 * (|strength| === 100) axis can contribute on its own — matches the old
 * single-mode editor's magnitude so "仅径向 +60" etc. still reads the same.
 *
 * EDITOR-RESULT-FIX-A1 (Bug B): this used to be a *signed*, bidirectional
 * offset — positive moved toward white, negative moved toward pure black
 * (0,0,0), regardless of `baseColor`. That silently "polluted" every
 * non-black base color with an increasing amount of literal black the
 * harder a gradient was cranked (e.g. a bright red base grew a near-black
 * cap at the dark end at high strength). The model is now lighten-only —
 * see `axisLightenOffset`. */
const AXIS_MAX_OFFSET = 0.5

/** Hard ceiling on the SUM of all enabled axes — this is what actually
 * prevents "径向+100 叠加 上下+100 叠加 左右+100" from ever crushing the
 * image to solid white/banding (spec 三.5): each axis alone maxes out
 * at `AXIS_MAX_OFFSET` (well under this), so the clamp only ever engages
 * once two or more axes are stacked at meaningfully large values. */
export const COMBINED_OFFSET_CLAMP = 0.55

function axisShapeFactor(pos01: number): number {
  return clamp((pos01 - 0.5) * 2, -1, 1)
}

/** Lighten-only per-axis contribution (Bug B fix). `shape` is the axis's
 * signed [-1, 1] shape factor (e.g. `axisShapeFactor(v)` for vertical);
 * `strength` is the raw -100..100 slider value.
 *
 * Whichever end of the axis has `shape * sign(strength) === -1` is the
 * ANCHOR — it always contributes exactly 0 regardless of `strength`'s
 * magnitude, so that end renders the untouched base color. The opposite end
 * (`shape * sign(strength) === +1`) contributes the full `AXIS_MAX_OFFSET`,
 * i.e. blends toward white. Flipping `strength`'s sign swaps which end is
 * the anchor; it never introduces a move toward black.
 *
 * Derivation: writing s = strength/100, the desired contribution at a given
 * `shape` is `AXIS_MAX_OFFSET/2 * (shape*sign(s) + 1)` scaled by `|s|`,
 * which simplifies (since `sign(s)*|s| === s`) to the branch-free form
 * below. */
function axisLightenOffset(shape: number, strength: number): number {
  const s = strength / 100
  return (AXIS_MAX_OFFSET / 2) * (shape * s + Math.abs(s))
}

/**
 * Combined lighten-only offset at normalized position (u, v) — both in
 * [0, 1], u = x/width, v = y/height. Always >= 0 (never moves toward black —
 * Bug B fix). This is the SINGLE shared math used by the editor's live
 * canvas preview, the final exported canvas, AND the backend
 * `background_synthesis.py` NumPy port (spec 三.4: "预览和最终生成
 * 必须一致") — any change here must be mirrored there.
 *
 * Direction contract (spec 三.1, revised for EDITOR-RESULT-FIX-A1):
 *   vertical:   strength < 0 -> 上浅下本色 | strength > 0 -> 上本色下浅
 *   horizontal: strength < 0 -> 左浅右本色 | strength > 0 -> 左本色右浅
 *   radial:     strength < 0 -> 中心本色周边浅 | strength > 0 -> 中心浅周边本色
 *   strength === 0 (any axis): that axis contributes nothing anywhere.
 * Disabled axes contribute nothing, regardless of their stored `strength`.
 */
export function combinedGradientOffset(u: number, v: number, gradients: BackgroundGradientConfig): number {
  let offset = 0
  if (gradients.vertical.enabled && gradients.vertical.strength !== 0) {
    offset += axisLightenOffset(axisShapeFactor(v), gradients.vertical.strength)
  }
  if (gradients.horizontal.enabled && gradients.horizontal.strength !== 0) {
    offset += axisLightenOffset(axisShapeFactor(u), gradients.horizontal.strength)
  }
  if (gradients.radial.enabled && gradients.radial.strength !== 0) {
    const dx = (u - 0.5) * 2
    const dy = (v - 0.5) * 2
    const d = Math.sqrt(dx * dx + dy * dy) // 0 at center, 1 at edge midpoints, ~1.41 at corners
    offset += axisLightenOffset(-axisShapeFactor(d), gradients.radial.strength)
  }
  return clamp(offset, 0, COMBINED_OFFSET_CLAMP)
}

/** Longest side (px) of the offscreen buffer `paintBackgroundGradients`
 * computes the per-pixel offset at. Gradients are inherently low-frequency,
 * so rendering at a small buffer and letting the canvas upscale it
 * (bilinearly) is visually indistinguishable from a full-resolution
 * per-pixel pass — but stays fast enough for real-time slider dragging, and
 * (unlike stacking multiple native CSS/Canvas gradient objects) can never
 * band or blow out, because every pixel goes through the same single clamp
 * in `combinedGradientOffset` (spec 三.4). */
const GRADIENT_BUFFER_LONG_SIDE = 128

/**
 * Paints the 色底图 background using the composable gradient model — radial /
 * vertical / horizontal axes are summed, not exclusive (spec 三). Falls back
 * to a flat fill instantly when every axis is disabled.
 */
export function paintBackgroundGradients(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  baseColor: string,
  gradients: BackgroundGradientConfig,
) {
  if (isSolidGradients(gradients)) {
    ctx.fillStyle = baseColor
    ctx.fillRect(0, 0, w, h)
    return
  }
  const long = GRADIENT_BUFFER_LONG_SIDE
  const bw = Math.max(8, Math.round(w >= h ? long : (long * w) / h))
  const bh = Math.max(8, Math.round(h >= w ? long : (long * h) / w))
  const buf = document.createElement('canvas')
  buf.width = bw
  buf.height = bh
  const bctx = buf.getContext('2d')
  if (!bctx) return
  const imgData = bctx.createImageData(bw, bh)
  const [br, bg, bb] = hexToRgb(baseColor)
  const data = imgData.data
  for (let y = 0; y < bh; y++) {
    const v = (y + 0.5) / bh
    for (let x = 0; x < bw; x++) {
      const u = (x + 0.5) / bw
      // Lighten-only (Bug B fix): `combinedGradientOffset` is always >= 0,
      // so the blend target is always white — the base color is never
      // blended toward black.
      const t = combinedGradientOffset(u, v, gradients)
      const idx = (y * bw + x) * 4
      data[idx] = br + (255 - br) * t
      data[idx + 1] = bg + (255 - bg) * t
      data[idx + 2] = bb + (255 - bb) * t
      data[idx + 3] = 255
    }
  }
  bctx.putImageData(imgData, 0, 0)
  ctx.save()
  ctx.imageSmoothingEnabled = true
  ctx.drawImage(buf, 0, 0, w, h)
  ctx.restore()
}

const ALPHA_BBOX_WORK_LONG_SIDE = 480
const ALPHA_BBOX_THRESHOLD = 8

export interface AlphaBBox {
  l: number
  t: number
  r: number
  b: number
}

/** Bounding box (in natural image pixel coords) of the transparent PNG's
 * non-transparent pixels. Matting engines typically export at the original
 * photo's full canvas size with a lot of transparent padding around the
 * actual subject, so "66% of the canvas's longest side" (spec 四) must be
 * measured against this bbox — never against the padded full image, or the
 * subject renders smaller than intended and off-center whenever the padding
 * is asymmetric. Downsamples for speed; only ever reads alpha. */
export function computeAlphaBBox(img: HTMLImageElement): AlphaBBox {
  const naturalWidth = img.naturalWidth || img.width
  const naturalHeight = img.naturalHeight || img.height
  const workScale = Math.min(1, ALPHA_BBOX_WORK_LONG_SIDE / Math.max(naturalWidth, naturalHeight))
  const w = Math.max(1, Math.round(naturalWidth * workScale))
  const h = Math.max(1, Math.round(naturalHeight * workScale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, w, h)
  const data = ctx.getImageData(0, 0, w, h).data
  let l = w
  let t = h
  let r = 0
  let b = 0
  let found = false
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > ALPHA_BBOX_THRESHOLD) {
        found = true
        if (x < l) l = x
        if (x > r) r = x
        if (y < t) t = y
        if (y > b) b = y
      }
    }
  }
  if (!found) return { l: 0, t: 0, r: naturalWidth, b: naturalHeight }
  return {
    l: Math.round(l / workScale),
    t: Math.round(t / workScale),
    r: Math.round((r + 1) / workScale),
    b: Math.round((b + 1) / workScale),
  }
}

// `loadHtmlImage` used to be defined here (and, identically, again in
// `DualPaneCutoutEditor.tsx`) — Editor Load Performance P0 (2026-08)
// consolidated both copies into one shared loader (with fetch/decode
// timing instrumentation + in-flight de-dupe) so all three chain editors
// share a single code path instead of three independent patches.
export { loadHtmlImage } from './mediaLoader'

/** Best-effort reconstruction of the Background Editor's placement formula
 * for chains whose 色底图 predates persisted editor params (see spec
 * 二十一.8) — deterministic, resolution-independent, and matches the
 * automatic pipeline's own default (`background_synthesis.SUBJECT_SCALE`
 * = 2/3, centered, square canvas) closely enough to be a safe fallback. */
export function inferredDefaultBackgroundParams() {
  return {
    ratioKey: '1:1',
    bgPreset: 'white' as BgPreset,
    bgCustomColor: '#ffffff',
    bgMode: 'solid' as BgMode,
    gradientIntensity: GRADIENT_DEFAULT,
    gradientVersion: GRADIENT_SCHEMA_VERSION,
    gradients: defaultGradients(),
    transform: { scalePct: DEFAULT_SCALE_PCT, offsetXFrac: 0, offsetYFrac: 0 },
  }
}
